import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import { getTargetGroupAttributes } from "../../actions/org/OrgTargetGroupActions";

// Components
import Spinner from "../spinner/Spinner";

// Components
function TgAttrLoading({ className = "" }) {
  return (
    <div
      className={`d-flex justify-content-center align-items-center bg-alt border p-1 ${className}`}
    >
      <Spinner />
      <span className="p-2">Loading, Please Wait...</span>
    </div>
  );
}

// PoiTypes Section
function PoiTypesSection({
  poiTypesMap = {},
  selectedPoiTypes = [],
  setSelectedPoiTypes = () => {},
}) {
  const [poiSearchedText, setPoiSearchedText] = useState("");

  // Filter POI types based on search
  // eslint-disable-next-line no-unused-vars
  const filteredPoiTypes = Object.entries(poiTypesMap).filter(([_, poi]) =>
    poi.name?.toLowerCase().includes(poiSearchedText.toLowerCase())
  );

  // function to handle select/deselect of POI type
  function handleSelectPoiType(isSelected, poiType) {
    if (isSelected) {
      setSelectedPoiTypes((prev) => prev.filter((type) => type !== poiType));
    } else {
      setSelectedPoiTypes((prev) => [...prev, poiType]);
    }

    // clear search text after selection
    setPoiSearchedText("");
  }

  return (
    <div className="border rounded p-3">
      <h6 className="mb-2 font-weight-bold">Add Behavioural POI Types</h6>

      {/* Search */}
      <input
        type="text"
        className="form-control"
        placeholder="Search POIs..."
        value={poiSearchedText}
        onChange={(e) => setPoiSearchedText(e.target.value)}
      />

      {/* Search Results */}
      {poiSearchedText && (
        <div className="border rounded mt-2">
          {filteredPoiTypes.length > 0 ? (
            filteredPoiTypes.map(([poiType, poi]) => {
              const { name, id } = poi;
              const isSelected = selectedPoiTypes.includes(poiType);

              return (
                <div
                  key={id}
                  className={`px-3 py-2 cursor-pointer ${
                    isSelected ? "bg-light text-primary" : ""
                  }`}
                  onClick={() => handleSelectPoiType(isSelected, poiType)}
                >
                  {name}
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-muted">No POIs found</div>
          )}
        </div>
      )}

      {/* Selected POITypes Chips */}
      <div className="d-flex flex-wrap gap-2 mt-2">
        {selectedPoiTypes.map((poiType) => {
          const poiTypeBean = poiTypesMap[poiType];
          const { id, name } = poiTypeBean || {};

          if (!id || !name) {
            return null;
          }

          return (
            <button
              key={id}
              className="btn btn-primary border m-1 px-3 shadow-none rounded-lg"
            >
              <label className="pl-1 mb-0">{name}</label>
              <span
                className="border rounded-circle ml-2 px-1"
                onClick={() => {
                  setSelectedPoiTypes((prev) =>
                    prev.filter((type) => type !== poiType)
                  );
                }}
              >
                x
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AttributesSection({
  targetGroupAttrs = {},
  selectedTgAttrs = [],
  setSelectedTgAttrs = () => {},
}) {
  return (
    <div>
      {Object.keys(targetGroupAttrs).map((attrKey, i) => {
        const attributes = targetGroupAttrs[attrKey];
        const attrGroupName = getAttrGroupNameByAttrKey(attrKey);

        return (
          <div key={i} className="border rounded p-3 mb-2">
            <h6 className="mb-0 font-weight-bold">{attrGroupName}</h6>

            <div className="row g-2">
              {attributes.map((attr, i) => {
                const { id, name } = attr;
                const isSelected = selectedTgAttrs.includes(id);
                return (
                  <div className="col-3 mt-2" key={i}>
                    <div
                      className={`border rounded-lg py-2 px-3 w-100 d-flex justify-content-between align-items-center ${
                        isSelected ? "border-primary" : ""
                      }`}
                    >
                      <span className="mr-1">{name}</span>
                      <input
                        type="checkbox"
                        name="attr"
                        className="cursor-pointer"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTgAttrs((prev) => [...prev, id]);
                          } else {
                            setSelectedTgAttrs((prev) =>
                              prev.filter((attrId) => attrId !== id)
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// functions
function getAttrGroupNameByAttrKey(attrKey) {
  const labelIdToNameMap = {
    GENDER: "Gender",
    AGE_GROUP: "Age Group",
    INCOME_GROUP: "Socio Economic Class (SEC)",
  };
  return labelIdToNameMap[attrKey] || attrKey;
}

/**
 * CampaignTargetGroupForm
 */
export default function CampaignTargetGroupForm({
  className = "",
  selectedTgAttrs = [],
  setSelectedTgAttrs = () => {},
  selectedPoiTypes = [],
  setSelectedPoiTypes = () => {},
}) {
  const dispatch = useDispatch();

  // Selector
  const { targetGroupAttrs = {}, poiTypesMap = {} } = useSelector(
    (state) => state.orgTargetGroup.originalData
  );
  const isGetTgAttrLoading = useSelector(
    (state) => state.orgTargetGroup.isGetTgAttrLoading
  );

  // Dispatch
  useEffect(() => {
    const secAttrNeeded = true;
    if (Object.keys(targetGroupAttrs).length === 0) {
      dispatch(getTargetGroupAttributes(secAttrNeeded));
    }
  }, [dispatch, Object.keys(targetGroupAttrs).length]);

  // loading
  if (isGetTgAttrLoading) {
    return <TgAttrLoading className="col-6 border rounded-lg shadow-sm" />;
  }

  return (
    <div
      className={`card h-100 col-6 border rounded-lg shadow-sm ${className}`}
    >
      <div className="card-body p-2">
        {/* Header */}
        <div className="mb-2">
          <h4 className="mb-1">
            Target Group <span className="text-muted">(Optional)</span>
          </h4>
          <p className="text-muted mb-0">
            Select demographic and interest attributes to refine your campaign
            analysis
          </p>
        </div>

        {/* Form */}
        <AttributesSection
          targetGroupAttrs={targetGroupAttrs}
          selectedTgAttrs={selectedTgAttrs}
          setSelectedTgAttrs={setSelectedTgAttrs}
        />

        {/* POI */}
        <PoiTypesSection
          poiTypesMap={poiTypesMap}
          selectedPoiTypes={selectedPoiTypes}
          setSelectedPoiTypes={setSelectedPoiTypes}
        />
      </div>
    </div>
  );
}
